<template>
  <v-dialog
    v-model="internalValue"
    max-width="900px"
    transition="dialog-bottom-transition"
    scrollable
  >
    <v-card class="ds-dialog">
      <v-card-title class="ds-dialog-header py-4">
        <v-icon left color="white" style="position: relative; z-index: 1">{{
          editMode ? "mdi-pencil" : "mdi-flag-checkered"
        }}</v-icon>
        <div class="header-text">
          <span class="ds-dialog-title">{{
            editMode ? "Editar Meta" : "Nova Meta"
          }}</span>
          <span class="header-subtitle">Objetivo, valores e prazo</span>
        </div>
        <v-spacer></v-spacer>
        <v-btn icon color="white" class="close-btn" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Sem altura fixa: o diálogo `scrollable` limita a 90% da tela e só
           rola o corpo quando ele não cabe (telas baixas). O formulário é
           compacto (campos `dense`, sem espaço reservado de hint) para caber
           inteiro num notebook comum. -->
      <v-card-text class="ds-dialog-body">
        <v-form ref="goalForm" v-model="validForm">
          <!-- Seção: Meta -->
          <section class="ds-form-section">
            <div class="ds-form-section__head">
              <div class="ds-form-section__title">
                <v-icon small color="primary" class="mr-2">mdi-information</v-icon>
                <span>Meta</span>
              </div>
            </div>
            <v-row dense>
              <v-col cols="12" md="7">
                <v-text-field
                  v-model="form.goal_name"
                  label="Nome da Meta"
                  outlined
                  dense
                  color="primary"
                  prepend-inner-icon="mdi-flag"
                  hide-details="auto"
                  :rules="[(v) => !!v || 'Nome da meta é obrigatório']"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="5">
                <!-- salvia-844: classificar a meta deixou de ser obrigatório. O
                     campo continua existindo e o "Outro" segue exigindo a
                     especificação quando escolhido — o que caiu foi só a
                     obrigatoriedade de escolher algum tipo. -->
                <v-autocomplete
                  v-model="form.goal_type"
                  :items="goalTypes"
                  label="Tipo de Meta (opcional)"
                  outlined
                  dense
                  color="primary"
                  prepend-inner-icon="mdi-target"
                  clearable
                  hide-details="auto"
                  no-data-text="Nenhum tipo encontrado"
                ></v-autocomplete>
              </v-col>

              <v-col cols="12" :md="form.goal_type === 'Outro' ? 7 : 12">
                <div class="status-field">
                  <span class="status-label">Status</span>
                  <!-- `mandatory` só com um status conhecido: com um valor
                       legado fora da lista, o Vuetify trocaria sozinho para o
                       primeiro botão e alteraria a meta sem ninguém pedir. -->
                  <v-btn-toggle
                    v-model="form.status"
                    :mandatory="statusOptions.includes(form.status)"
                    :color="statusMeta.color"
                    dense
                    class="status-toggle"
                  >
                    <v-btn
                      v-for="s in statusOptions"
                      :key="s"
                      :value="s"
                      small
                      text
                    >
                      <v-icon x-small left>{{ statusIcons[s] }}</v-icon>
                      {{ s }}
                    </v-btn>
                  </v-btn-toggle>
                </div>
              </v-col>

              <!-- Quando "Outro" é escolhido, abre campo para especificar o tipo -->
              <v-col v-if="form.goal_type === 'Outro'" cols="12" md="5">
                <v-text-field
                  v-model="otherGoalType"
                  label="Especifique o tipo de meta"
                  outlined
                  dense
                  color="primary"
                  prepend-inner-icon="mdi-pencil-outline"
                  hide-details="auto"
                  :rules="[
                    (v) =>
                      form.goal_type !== 'Outro' ||
                      !!(v && v.trim()) ||
                      'Especifique o tipo de meta',
                  ]"
                ></v-text-field>
              </v-col>
            </v-row>
          </section>

          <v-row dense class="mt-3">
            <!-- Seção: Valores e Medidas -->
            <v-col cols="12" md="7">
              <section class="ds-form-section fill-height">
                <div class="ds-form-section__head">
                  <div class="ds-form-section__title">
                    <v-icon small color="primary" class="mr-2">mdi-chart-line</v-icon>
                    <span>Valores</span>
                    <span class="ds-form-section__optional">opcional</span>
                  </div>
                  <span v-if="progressText" class="ds-form-section__aside">
                    {{ progressText }}
                  </span>
                </div>
                <v-row dense>
                  <v-col cols="12" sm="4">
                    <v-text-field
                      v-model.number="form.current_value"
                      label="Valor Atual"
                      type="number"
                      outlined
                      dense
                      color="primary"
                      :suffix="form.unit || ''"
                      hide-details="auto"
                      :rules="[numberRule]"
                    ></v-text-field>
                  </v-col>

                  <v-col cols="12" sm="4">
                    <v-text-field
                      v-model.number="form.target_value"
                      label="Valor Alvo"
                      type="number"
                      outlined
                      dense
                      color="primary"
                      :suffix="form.unit || ''"
                      hide-details="auto"
                      :rules="[numberRule]"
                    ></v-text-field>
                  </v-col>

                  <v-col cols="12" sm="4">
                    <v-select
                      v-model="form.unit"
                      :items="unitOptions"
                      label="Medida"
                      outlined
                      dense
                      color="primary"
                      hide-details="auto"
                      clearable
                    ></v-select>
                  </v-col>
                </v-row>
              </section>
            </v-col>

            <!-- Seção: Período -->
            <v-col cols="12" md="5">
              <section class="ds-form-section fill-height">
                <div class="ds-form-section__head">
                  <div class="ds-form-section__title">
                    <v-icon small color="primary" class="mr-2">mdi-calendar-range</v-icon>
                    <span>Período</span>
                  </div>
                  <span v-if="durationText" class="ds-form-section__aside">
                    {{ durationText }}
                  </span>
                </div>
                <v-row dense>
                  <v-col cols="12" sm="6">
                    <v-menu
                      ref="startDateMenu"
                      v-model="startDateMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          :value="formatDate(form.start_date)"
                          label="Início"
                          prepend-inner-icon="mdi-calendar-start"
                          readonly
                          outlined
                          dense
                          color="primary"
                          hide-details="auto"
                          v-bind="attrs"
                          v-on="on"
                          :rules="[(v) => !!v || 'Data de início é obrigatória']"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="form.start_date"
                        @input="startDateMenu = false"
                        no-title
                        scrollable
                      ></v-date-picker>
                    </v-menu>
                  </v-col>

                  <v-col cols="12" sm="6">
                    <v-menu
                      ref="targetDateMenu"
                      v-model="targetDateMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          :value="formatDate(form.target_date)"
                          label="Data Alvo"
                          prepend-inner-icon="mdi-calendar-check"
                          readonly
                          outlined
                          dense
                          color="primary"
                          hide-details="auto"
                          v-bind="attrs"
                          v-on="on"
                          :rules="[
                            () => {
                              if (!form.target_date) return true;
                              return new Date(form.target_date) >=
                                new Date(form.start_date)
                                ? true
                                : 'Data alvo deve ser após a data de início';
                            },
                          ]"
                        />
                      </template>
                      <v-date-picker
                        v-model="form.target_date"
                        @input="targetDateMenu = false"
                        no-title
                        scrollable
                        :min="form.start_date"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>
                </v-row>
              </section>
            </v-col>
          </v-row>

          <!-- Seção: Observações -->
          <section class="ds-form-section mt-3">
            <div class="ds-form-section__head">
              <div class="ds-form-section__title">
                <v-icon small color="primary" class="mr-2">mdi-note-text</v-icon>
                <span>Observações</span>
                <span class="ds-form-section__optional">opcional</span>
              </div>
              <span
                class="ds-form-section__aside"
                :class="{ 'ds-form-section__aside--alert': notesLength > 500 }"
              >
                {{ notesLength }}/500
              </span>
            </div>
            <v-textarea
              v-model="form.notes"
              placeholder="Combinados, estratégia, cuidados…"
              outlined
              dense
              color="primary"
              auto-grow
              rows="2"
              row-height="20"
              prepend-inner-icon="mdi-pencil"
              hide-details="auto"
              :rules="[
                (v) => !v || v.length <= 500 || 'Máximo de 500 caracteres',
              ]"
            ></v-textarea>
          </section>
        </v-form>
      </v-card-text>

      <v-card-actions class="ds-dialog-actions">
        <v-btn outlined color="primary" @click="close">
          <v-icon left>mdi-close</v-icon>
          Cancelar
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          depressed
          @click="saveGoal"
          :disabled="!validForm || isSaving"
          :loading="isSaving"
        >
          <v-icon left>{{ editMode ? "mdi-content-save" : "mdi-plus" }}</v-icon>
          Salvar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import http from "@/services/http";
import { useSnackbar } from "@/composables/useSnackbar";
import { formatDate, todayISO } from "@/utils/datetime";

export default {
  name: "GoalFormDialog",

  props: {
    value: { type: Boolean, default: false },
    patientId: { type: [String, Number], required: true },
    // Meta a editar; null/ausente = modo de criação.
    goal: { type: Object, default: null },
  },

  setup() {
    const { success, error, erroDeRequisicao } = useSnackbar();
    return { success, error, erroDeRequisicao };
  },

  data() {
    return {
      form: this.blankForm(),
      otherGoalType: "",
      validForm: false,
      isSaving: false,
      startDateMenu: false,
      targetDateMenu: false,
      goalTypes: [
        "Perda de Peso",
        "Ganho de Peso",
        "Hipertrofia Muscular",
        "Tonificação",
        "Definição Muscular",
        "Resistência Cardiovascular",
        "Resistência Muscular",
        "Flexibilidade",
        "Mobilidade",
        "Saúde Metabólica",
        "Controle de Pressão Arterial",
        "Controle de Glicemia",
        "Controle de Colesterol",
        "Controle de Triglicerídeos",
        "Hidratação",
        "Redução de Gordura Corporal",
        "Aumento de Massa Magra",
        "Melhora Postural",
        "Aumento de Força",
        "Aumento de Potência",
        "Redução de IMC",
        "Ganho de Energia",
        "Melhora do Sono",
        "Redução de Estresse",
        "Aumento de Metabolismo",
        "Recuperação de Lesão",
        "Condicionamento Físico Geral",
        "Performance Esportiva",
        "Qualidade de Vida",
        "Outro",
      ],
      unitOptions: [
        "%", "bpm", "cm", "g", "h", "kcal", "kg", "km", "L", "lb", "m",
        "mg/dL", "min", "mL", "mm", "mmHg", "mmol/L", "passos", "pol", "pts",
        "quantidade", "rep", "unidade", // "unidade" = contagem (SALVIA-941); "quantidade" (SALVIA-954)
      ],
      statusOptions: ["Não Iniciado", "Em Andamento", "Concluído", "Cancelado"],
      statusIcons: {
        "Não Iniciado": "mdi-circle-outline",
        "Em Andamento": "mdi-progress-clock",
        Concluído: "mdi-check-circle",
        Cancelado: "mdi-close-circle",
      },
    };
  },

  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },

    editMode() {
      return !!(this.goal && this.goal.id);
    },

    /** Cor do botão de status ativo — a mesma leitura das cores do sistema. */
    statusMeta() {
      const colors = {
        "Não Iniciado": "grey darken-1",
        "Em Andamento": "primary",
        Concluído: "success",
        Cancelado: "error",
      };
      return { color: colors[this.form.status] || "primary" };
    },

    /** "80 → 72 kg (−8)": resumo dos valores no título da seção. */
    progressText() {
      const cur = this.form.current_value;
      const tgt = this.form.target_value;
      if (!this.isNumber(cur) || !this.isNumber(tgt)) return "";
      const fmt = (n) => Number(n).toLocaleString("pt-BR", { maximumFractionDigits: 2 });
      const unit = this.form.unit ? ` ${this.form.unit}` : "";
      const diff = Number(tgt) - Number(cur);
      const sign = diff > 0 ? "+" : diff < 0 ? "−" : "±";
      return `${fmt(cur)} → ${fmt(tgt)}${unit} (${sign}${fmt(Math.abs(diff))})`;
    },

    /** Prazo entre início e data alvo, em dias ou meses aproximados. */
    durationText() {
      const { start_date: start, target_date: end } = this.form;
      if (!start || !end) return "";
      const toUTC = (iso) => {
        const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
        return Date.UTC(y, m - 1, d);
      };
      const days = Math.round((toUTC(end) - toUTC(start)) / 86400000);
      if (!Number.isFinite(days) || days < 0) return "";
      if (days < 60) return `${days} ${days === 1 ? "dia" : "dias"}`;
      const months = Math.round(days / 30.44);
      return `≈ ${months} meses`;
    },

    notesLength() {
      return (this.form.notes || "").length;
    },
  },

  watch: {
    value(open) {
      if (open) {
        this.form = this.fromGoal();
        // Se o tipo salvo não está na lista predefinida, é um "Outro"
        // customizado: joga o valor no campo de texto e marca "Outro".
        this.otherGoalType = "";
        if (this.form.goal_type && !this.goalTypes.includes(this.form.goal_type)) {
          this.otherGoalType = this.form.goal_type;
          this.form.goal_type = "Outro";
        }
        this.$nextTick(() => {
          if (this.$refs.goalForm) this.$refs.goalForm.resetValidation();
        });
      }
    },
  },

  methods: {
    formatDate,
    blankForm() {
      return {
        id: null,
        patient_id: this.patientId || "",
        goal_name: "",
        goal_type: "",
        target_value: null,
        current_value: null,
        unit: "",
        start_date: todayISO(),
        target_date: "",
        notes: "",
        status: "Em Andamento",
      };
    },

    fromGoal() {
      if (this.goal && this.goal.id) {
        return { ...this.blankForm(), ...this.goal, patient_id: this.patientId };
      }
      return this.blankForm();
    },

    isNumber(v) {
      return v !== null && v !== "" && v !== undefined && !isNaN(parseFloat(v));
    },

    numberRule(v) {
      return v === null || v === "" || !isNaN(parseFloat(v)) || "Deve ser um número";
    },

    async saveGoal() {
      const isValid = await this.$refs.goalForm.validate();
      if (!isValid) return;

      // Se "Outro", envia o texto digitado como tipo da meta.
      const payload = { ...this.form };
      if (payload.goal_type === "Outro") {
        payload.goal_type = (this.otherGoalType || "").trim();
      }

      this.isSaving = true;
      try {
        if (this.editMode) {
          await http.put(`/api/patient-goals/edit/${payload.id}`, payload);
          this.success("Meta atualizada com sucesso!");
        } else {
          await http.post(`/api/patient-goals/create`, payload);
          this.success("Meta criada com sucesso!");
        }
        this.$emit("saved", payload);
        this.close();
      } catch (err) {
        console.error("Erro ao salvar meta:", err);
        this.erroDeRequisicao(err, "Erro ao salvar meta");
      } finally {
        this.isSaving = false;
      }
    },

    close() {
      this.internalValue = false;
    },
  },
};
</script>

<style scoped>
.ds-dialog {
  border-radius: 4px !important;
  overflow: hidden;
}
.ds-dialog-header {
  background: linear-gradient(135deg, var(--ds-primary) 0%, var(--ds-primary-deep) 100%);
  color: #fff;
}
.ds-dialog-title {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.3px;
  position: relative;
  z-index: 1;
}
.ds-dialog-header :deep(.v-btn) {
  position: relative;
  z-index: 1;
}
.header-text {
  display: flex;
  flex-direction: column;
  line-height: 1.25;
  position: relative;
  z-index: 1;
}
.header-subtitle {
  font-size: 12px;
  font-weight: 400;
  opacity: 0.85;
}
/* Status como seletor segmentado: um clique, e a cor já diz o estado. */
.status-field {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  min-height: 40px;
}
.status-label {
  font-size: 13px;
  color: var(--ds-text-soft);
}
.status-toggle {
  flex-wrap: wrap;
  border: 1px solid var(--ds-blue-100) !important;
}
.status-toggle :deep(.v-btn) {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 500;
}
</style>
